// Copyright 2026, Auxilio Technologies LLC

import { useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { Roles } from '../config/rolesConfig';

/**
 * Hook to determine if the current user is a Site Manager with a specific site assigned.
 * Site Managers with an assigned site should:
 * 1. Have their site pre-selected in all site dropdowns
 * 2. Not be able to view or select other sites
 * 3. Only see data for their assigned site
 */
export function useSiteRestriction() {
  const user = useAuthStore((state) => state.user);

  return useMemo(() => {
    const isSiteManager = user?.role === Roles.SITE_MANAGER;
    const hasAssignedSite = Boolean(user?.siteId);
    const isRestricted = isSiteManager && hasAssignedSite;

    return {
      // True if user is a Site Manager with an assigned site
      isRestricted,
      // The site ID the user is restricted to (or undefined)
      restrictedSiteId: isRestricted ? user?.siteId : undefined,
      // True if user is a Site Manager (regardless of site assignment)
      isSiteManager,
      // True if user has an assigned site
      hasAssignedSite,
      // The user's assigned site ID (even if not restricted)
      userSiteId: user?.siteId,
    };
  }, [user?.role, user?.siteId]);
}
